'use client'

import { useEffect, useState } from 'react'
import { useAuth, useOrganization } from '@clerk/nextjs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { ConfirmationModal } from '@/components/ui/confirmation-modal'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { SettingsSection } from '@/features/settings-modal/settings-section'
import { useRemoveMemberMutation } from '@/api/organization/members/remove-member-mutation'
import { useUpdateOrganizationMutation } from '@/api/organization/update-organization-mutation'
import { useIsSuperUser } from '@/utils/clerk/use-is-super-user'
import { toast } from '@/features/toast/toast'
import { useGetMembersPolicyQuery } from '@/api/organization/members/get-members-policy-query'
import { getWorkspaceLeaveBlockedReason } from '../utils/workspace-leave-policy'
import { getApiErrorMessage } from '@/utils/api/get-api-error-message'

export const GeneralSection = () => {
    const { userId, orgRole } = useAuth()
    const { organization } = useOrganization()
    const isSuperUser = !!useIsSuperUser()
    const canManage = orgRole === 'org:admin' || isSuperUser
    const [name, setName] = useState(organization?.name ?? '')
    const updateOrganizationMutation = useUpdateOrganizationMutation()

    useEffect(() => {
        if (organization?.name) setName(organization.name)
    }, [organization?.name])
    const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false)
    const removeMemberMutation = useRemoveMemberMutation()
    const { data: membersPolicy } = useGetMembersPolicyQuery()
    const cannotLeaveReason = getWorkspaceLeaveBlockedReason(membersPolicy)

    const hasChanges = name !== (organization?.name ?? '')

    const handleSave = () => {
        if (!organization || !name.trim()) return
        updateOrganizationMutation.mutate(
            { name: name.trim() },
            {
                onSuccess: async () => {
                    await organization.reload()
                    toast.success('Workspace name updated')
                },
                onError: () => {
                    toast.error('Failed to update workspace name')
                },
            }
        )
    }

    const handleLeave = () =>
        new Promise<void>((resolve, reject) => {
            if (!userId) return reject()
            removeMemberMutation.mutate(
                { userId },
                {
                    onSuccess: () => {
                        window.location.href = '/routing'
                        resolve()
                    },
                    onError: (error) => {
                        toast.error(getApiErrorMessage(error, 'Failed to leave workspace'))
                        reject()
                    },
                }
            )
        })

    return (
        <>
            <SettingsSection title="Workspace profile">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <Avatar className="h-12 w-12 shrink-0">
                        <AvatarImage src={organization?.imageUrl ?? undefined} alt={organization?.name ?? ''} />
                        <AvatarFallback>{organization?.name?.charAt(0)?.toUpperCase() ?? 'W'}</AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col gap-1.5 flex-1">
                        <label className="text-sm font-medium text-foreground">Workspace name</label>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                disabled={!canManage}
                                className="sm:max-w-[300px]"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && hasChanges) handleSave()
                                }}
                            />
                            {canManage && (
                                <Button
                                    variant="ds2-primary"
                                    size="ds2"
                                    onClick={handleSave}
                                    disabled={!hasChanges || !name.trim() || updateOrganizationMutation.isPending}
                                    isLoading={updateOrganizationMutation.isPending}
                                    className="w-fit"
                                >
                                    Save
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </SettingsSection>

            <SettingsSection title="Danger zone">
                <div className="rounded-lg border border-destructive/20 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-medium">Leave workspace</span>
                        <span className="text-sm text-muted-foreground">
                            You will lose access to this workspace and all its data.
                        </span>
                    </div>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span tabIndex={cannotLeaveReason ? 0 : -1} className="inline-flex">
                                <Button
                                    variant="ds2-destructive"
                                    size="ds2"
                                    onClick={() => setLeaveConfirmOpen(true)}
                                    disabled={!!cannotLeaveReason}
                                >
                                    Leave workspace
                                </Button>
                            </span>
                        </TooltipTrigger>
                        {cannotLeaveReason && (
                            <TooltipContent className="max-w-[260px] text-left">{cannotLeaveReason}</TooltipContent>
                        )}
                    </Tooltip>
                </div>
            </SettingsSection>

            <ConfirmationModal
                open={leaveConfirmOpen}
                onOpenChange={setLeaveConfirmOpen}
                title="Leave workspace?"
                subtitle="This action cannot be undone."
                content="You will lose access to this workspace and all its data."
                confirmText="Leave workspace"
                onConfirm={handleLeave}
                destructive
            />
        </>
    )
}
